// src/pages/Loads/LoadsMapView.jsx
import React, { useMemo, useState } from "react";
import { GoogleMap, LoadScript, Marker } from "@react-google-maps/api";

const containerStyle = {
    width: "100%",
    height: "420px",
};

const DEFAULT_CENTER = {
    lat: 39.8283, // centro aproximado USA
    lng: -98.5795,
};

const LoadsMapView = ({ loads = [], loading }) => {
    const [selectedLoadId, setSelectedLoadId] = useState(null);
    const [selectedStop, setSelectedStop] = useState(null); // 👈 nuevo

    // Flatten de todos los stops con info del load
    const { allStops, center } = useMemo(() => {
        const stops = [];

        loads.forEach((load) => {
            (load.stops || []).forEach((stop) => {
                if (
                    stop.lat !== null &&
                    stop.lat !== undefined &&
                    stop.lng !== null &&
                    stop.lng !== undefined
                ) {
                    stops.push({
                        ...stop,
                        load_id: load.id,
                        load_number: load.number || load.id,
                        client_name: load.client?.name || "",
                        status_name: load.status?.name || "",
                    });
                }
            });
        });

        // Si hay stops, centro en el primero; si no, default
        const firstStop = stops[0];
        const centerCoords =
            firstStop && firstStop.lat && firstStop.lng
                ? { lat: parseFloat(firstStop.lat), lng: parseFloat(firstStop.lng) }
                : DEFAULT_CENTER;

        return {
            allStops: stops,
            center: centerCoords,
        };
    }, [loads]);

    const visibleStops = useMemo(() => {
        if (!selectedLoadId) return allStops;
        return allStops.filter((s) => s.load_id === selectedLoadId);
    }, [allStops, selectedLoadId]);

    const selectedLoad = useMemo(
        () => loads.find((l) => l.id === selectedLoadId),
        [loads, selectedLoadId]
    );

    const handleLoadClick = (load) => {
        const isSelected = load.id === selectedLoadId;
        setSelectedLoadId(isSelected ? null : load.id);
        setSelectedStop(null); // al cambiar de load, limpiamos stop seleccionado
    };

    const formatDateTime = (dateString, timeString) => {
        if (!dateString && !timeString) return "No date/time";
        // si viene "2025-12-11 03:58:00" puedes adaptarlo, por ahora lo mostramos simple
        if (dateString && !timeString) return dateString;
        if (!dateString && timeString) return timeString;
        return `${dateString} ${timeString}`;
    };

    const LOAD_COLORS = [
        "#1E88E5", "#43A047", "#FB8C00", "#8E24AA",
        "#F4511E", "#3949AB", "#6D4C41", "#00897B",
        "#C0CA33", "#5E35B1"
    ];

    const loadColors = useMemo(() => {
        const map = {};
        loads.forEach((load, idx) => {
            map[load.id] = LOAD_COLORS[idx % LOAD_COLORS.length];
        });
        return map;
    }, [loads]);

    const getMarkerIcon = (color) => ({
        path: "M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5c-1.4 0-2.5-1.1-2.5-2.5S10.6 6.5 12 6.5s2.5 1.1 2.5 2.5S13.4 11.5 12 11.5z",
        fillColor: color,
        fillOpacity: 1,
        strokeColor: "#FFFFFF",
        strokeWeight: 2,
        scale: 1.5,
        
    });

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Mapa + detalle de stop */}
            <div className="lg:col-span-2 flex flex-col gap-3">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-sm font-semibold text-cs-dark">
                            Loads route overview
                        </h2>
                        <p className="text-[11px] text-cs-muted">
                            Visualize all stops on the map. Select a load on the right or
                            click a stop marker for details.
                        </p>
                    </div>
                    <div className="text-[11px] text-cs-muted">
                        {visibleStops.length} stops shown
                    </div>
                </div>

                <div className="border border-cs-border rounded-2xl overflow-hidden bg-cs-bg-alt">
                    {loading ? (
                        <div className="flex items-center justify-center h-[420px] text-sm text-cs-muted gap-2">
                            <span className="material-symbols-outlined animate-spin text-base">
                                progress_activity
                            </span>
                            Loading map data…
                        </div>
                    ) : (
                        <LoadScript
                            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_KEY || "AIzaSyD2ihNBZrNGhWURi4BSk53T5lsgrJ08ciA"}
                        >
                            <GoogleMap
                                mapContainerStyle={containerStyle}
                                center={center}
                                zoom={4}
                            >
                                {visibleStops.map((stop, idx) => (
                                    <Marker
                                        key={`${stop.load_id}-${stop.order}-${idx}`}
                                        position={{
                                            lat: parseFloat(stop.lat),
                                            lng: parseFloat(stop.lng),
                                        }}
                                        icon={getMarkerIcon(loadColors[stop.load_id])}
                                        label={{
                                            text: String(stop.order),
                                            color: "#FFFFFF",
                                            fontSize: "11px",
                                            fontWeight: "bold",
                                        }}
                                        onClick={() => setSelectedStop(stop)}
                                    />
                                ))}
                            </GoogleMap>
                        </LoadScript>
                    )}
                </div>

                {/* Detalle del stop seleccionado */}
                {selectedStop && (
                    <div className="border border-cs-border rounded-2xl bg-cs-bg px-4 py-3 shadow-sm">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-semibold text-cs-dark flex items-center gap-1">
                                    <span className="material-symbols-outlined text-sm">
                                        location_on
                                    </span>
                                    Stop #{selectedStop.order} –{" "}
                                    <span className="truncate max-w-[240px]">
                                        {selectedStop.name}
                                    </span>
                                </h3>
                                <p className="text-[11px] text-cs-muted mt-0.5">
                                    Load #{selectedStop.load_number} –{" "}
                                    {selectedStop.client_name || "No client"}
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setSelectedStop(null)}
                                className="h-7 w-7 rounded-full border border-cs-border flex items-center justify-center bg-cs-bg hover:bg-cs-bg-alt"
                            >
                                <span className="material-symbols-outlined text-[14px] text-cs-muted">
                                    close
                                </span>
                            </button>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 text-[11px]">
                            <div className="space-y-1">
                                <div className="flex items-center gap-1 text-cs-muted">
                                    <span className="material-symbols-outlined text-[14px]">
                                        route
                                    </span>
                                    <span>
                                        Miles from previous stop:{" "}
                                        <span className="font-semibold text-cs-dark">
                                            {selectedStop.mile ?? 0} mi
                                        </span>
                                    </span>
                                </div>

                                <div>
                                    <span className="block text-[10px] uppercase tracking-wide text-cs-muted">
                                        Load time
                                    </span>
                                    <span className="text-cs-dark">
                                        {formatDateTime(
                                            selectedStop.date,
                                            selectedStop.load_time
                                        )}
                                    </span>
                                </div>

                                <div>
                                    <span className="block text-[10px] uppercase tracking-wide text-cs-muted">
                                        Delivery time
                                    </span>
                                    <span className="text-cs-dark">
                                        {formatDateTime(
                                            selectedStop.delivery_date,
                                            selectedStop.delivery_time
                                        )}
                                    </span>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <div>
                                    <span className="block text-[10px] uppercase tracking-wide text-cs-muted mb-0.5">
                                        Stop status
                                    </span>
                                    <div className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-cs-border bg-cs-bg-alt">
                                        <span
                                            className="inline-block w-2 h-2 rounded-full"
                                            style={{
                                                backgroundColor:
                                                    selectedStop.status?.color || "#6B7280",
                                            }}
                                        />
                                        <span className="text-[11px] text-cs-dark">
                                            {selectedStop.status?.name || "No status"}
                                        </span>
                                    </div>
                                </div>

                                <div>
                                    <span className="block text-[10px] uppercase tracking-wide text-cs-muted mb-0.5">
                                        Operator status
                                    </span>
                                    <div className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 border border-cs-border bg-cs-bg-alt">
                                        <span
                                            className="inline-block w-2 h-2 rounded-full"
                                            style={{
                                                backgroundColor:
                                                    selectedStop.statusOperator?.color || "#6B7280",
                                            }}
                                        />
                                        <span className="text-[11px] text-cs-dark">
                                            {selectedStop.statusOperator?.name || "No status"}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-1">
                                    <span className="block text-[10px] uppercase tracking-wide text-cs-muted mb-0.5">
                                        Geofence
                                    </span>
                                    <span className="text-cs-dark">
                                        {selectedStop.geofence
                                            ? `Enabled (${selectedStop.geofence} radius units)`
                                            : "No geofence configured"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {Array.isArray(selectedStop.evidences) &&
                            selectedStop.evidences.length > 0 && (
                                <div className="mt-3 border-t border-cs-border pt-2 text-[11px] text-cs-muted">
                                    Evidences attached:{" "}
                                    <span className="font-semibold text-cs-dark">
                                        {selectedStop.evidences.length}
                                    </span>
                                </div>
                            )}
                    </div>
                )}
            </div>

            {/* Panel lateral de loads */}
            <div className="border border-cs-border rounded-2xl bg-cs-bg p-3 flex flex-col">
                <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-cs-dark">Loads list</h3>
                    <span className="text-[11px] text-cs-muted">
                        {loads.length} loads
                    </span>
                </div>

                <div className="flex-1 overflow-y-auto space-y-2">
                    {loads.length === 0 && (
                        <div className="text-xs text-cs-muted text-center py-6">
                            No loads found with current filters.
                        </div>
                    )}

                    {loads.map((load) => {
                        const isSelected = load.id === selectedLoadId;
                        const stopsCount = load.stops?.length || 0;
                        const miles = load.practical_miles || 0;

                        return (
                            <button
                                key={load.id}
                                type="button"
                                onClick={() => handleLoadClick(load)}
                                className={`
                  w-full text-left rounded-xl border px-3 py-2
                  text-xs mb-1
                  ${isSelected
                                        ? "border-cs-primary bg-cs-surface-alt"
                                        : "border-cs-border bg-cs-bg hover:bg-cs-bg-alt"
                                    }
                  transition-colors
                `}
                            >
                                <div className="flex items-center justify-between gap-2 mb-1">
                                    <span className="font-semibold text-cs-dark">
                                        Load #{load.number || load.id}
                                    </span>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-cs-bg-alt text-cs-muted border border-cs-border">
                                        {load.status?.name || "No status"}
                                    </span>
                                </div>

                                <div className="text-[11px] text-cs-muted truncate">
                                    {load.client?.name || "No client"}
                                </div>

                                <div className="flex items-center gap-3 mt-1 text-[11px] text-cs-muted">
                                    <span>
                                        <span className="material-symbols-outlined align-middle text-[14px] mr-1">
                                            route
                                        </span>
                                        {stopsCount} stops
                                    </span>
                                    <span>
                                        <span className="material-symbols-outlined align-middle text-[14px] mr-1">
                                            distance
                                        </span>
                                        {miles} mi
                                    </span>
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="mt-2 text-[10px] text-cs-muted">
                    Tip: click on a load to focus its stops on the map, or click on a
                    stop marker to see detailed information.
                </div>
            </div>
        </div>
    );
};

export default LoadsMapView;
